import { Box } from "@mui/material";
import { FC, useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTypedTranslation } from "../../../hooks";
import { CheckboxInput } from "../../Form";
import { BodyTextM, HeadingS } from "../../Typography";
import { FormValues } from "./EditInvestmentStrategyPage";
import * as Styled from "./EditInvestmentStrategyPage.styled";

type EditInvestmentStrategyConfirmBoxProps = {
    fundsWithAllocationRecommendations:
        | {
              maxPercentageAllocation: number;
              id: number;
              name: string;
              description: string;
          }[]
        | undefined;
};

export const EditInvestmentStrategyConfirmBox: FC<EditInvestmentStrategyConfirmBoxProps> = ({
    fundsWithAllocationRecommendations,
}) => {
    const { t } = useTypedTranslation();

    const { trigger, getValues } = useFormContext<FormValues>();

    const calculatedAgreeNeeded = useWatch<FormValues, "funds", FormValues["funds"], boolean>({
        name: "funds",
        compute: (funds: FormValues["funds"]) => {
            return funds.some((fund) => {
                const recommendedFund = fundsWithAllocationRecommendations?.find((f) => f.id === fund.id);
                return Number(fund.allocation) > Number(recommendedFund?.maxPercentageAllocation ?? 0);
            });
        },
    });

    const watchedAgree = useWatch<FormValues, "agree">({
        name: "agree",
    });

    // validate funds allocation when agree checked changes
    useEffect(() => {
        getValues("funds").forEach((_, index) => {
            trigger(`funds.${index}.allocation`);
        });
    }, [getValues, trigger, watchedAgree]);

    if (!calculatedAgreeNeeded) return null;

    return (
        <>
            <Box>
                <HeadingS>
                    {t(`investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.overrideTitle`)}
                </HeadingS>

                <BodyTextM>
                    {t(`investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.overrideDescription`)}
                </BodyTextM>
            </Box>

            <Styled.CardPaper variant="outlined">
                <BodyTextM>
                    {t(`investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.overrideDisclaimer`)}
                </BodyTextM>
                <CheckboxInput
                    name="agree"
                    label={t(`investmentStrategy.pensionQuestionnaire.editInvestmentStrategyPage.common.overrideLabel`)}
                />
            </Styled.CardPaper>
        </>
    );
};
